import { useMemo } from "react";
import type {
	ColDef,
	ICellRendererParams,
	ValueFormatterParams,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import {
	HEADER_PAD_CLASS,
	NUMBER_CELL_CLASS,
	NUMBER_CELL_ROOMY_CLASS,
	ONE_LINE_CELL_CLASS,
	OneLineCellStyles,
} from "@/controls/ag-grid/OneLineCells";
import type { BarrelLayout } from "../models/Barrel";

interface ActionsCellRendererParams extends ICellRendererParams<BarrelLayout> {
	onEdit?: (layout: BarrelLayout) => void;
	onDelete?: (layout: BarrelLayout) => void;
}

const ActionsCellRenderer = (params: ActionsCellRendererParams) => {
	const { data, onEdit, onDelete } = params;

	if (!data) return null;

	return (
		<Box
			sx={{
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				gap: 0.5,
				height: "100%",
			}}
		>
			<Tooltip title="Edit pass">
				<IconButton
					size="small"
					onClick={() => onEdit?.(data)}
					aria-label="edit pass"
					color="primary"
					sx={{ p: 0.25 }}
				>
					<EditIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Remove pass">
				<IconButton
					size="small"
					onClick={() => onDelete?.(data)}
					aria-label="remove pass"
					sx={{ p: 0.25, color: "error.main" }}
				>
					<DeleteIcon fontSize="small" />
				</IconButton>
			</Tooltip>
		</Box>
	);
};

interface BarrelLayoutsTableProps {
	layouts: BarrelLayout[];
	isEditing?: boolean;
	onEdit?: (layout: BarrelLayout) => void;
	onDelete?: (layout: BarrelLayout) => void;
}

export const BarrelLayoutsTable = ({
	layouts,
	isEditing = false,
	onEdit,
	onDelete,
}: BarrelLayoutsTableProps) => {
	const columnDefs: ColDef<BarrelLayout>[] = useMemo(
		() => [
			{
				field: "Position",
				headerName: "#",
				width: 50,
				sort: "asc",
				cellClass: ONE_LINE_CELL_CLASS,
			},
			{
				field: "PassName",
				headerName: "Pass Id",
				flex: 1,
				minWidth: 140,
				cellClass: ONE_LINE_CELL_CLASS,
			},
			{
				field: "PassScheduledTonnage",
				headerName: "Sched Tons",
				width: 105,
				cellClass: [
					ONE_LINE_CELL_CLASS,
					NUMBER_CELL_CLASS,
					NUMBER_CELL_ROOMY_CLASS,
				],
				valueFormatter: (params: ValueFormatterParams<BarrelLayout>) =>
					params.value != null
						? params.value.toLocaleString("en-US", {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							})
						: "",
			},
			...(isEditing
				? [
						{
							headerName: "",
							width: 70,
							cellRenderer: ActionsCellRenderer,
							cellRendererParams: { onEdit, onDelete },
							sortable: false,
							filter: false,
							suppressSizeToFit: true,
						} as ColDef<BarrelLayout>,
					]
				: []),
		],
		[isEditing, onEdit, onDelete],
	);

	// One line per pass, so the rows keep their height and the grid fills the tab.
	const defaultColDef = useMemo(
		() => ({
			...globalDefaultColDef,
			sortable: false,
			filter: false,
			wrapText: false,
			autoHeight: false,
			headerClass: HEADER_PAD_CLASS,
		}),
		[],
	);

	if (layouts.length === 0) {
		return (
			<Box
				sx={{
					flex: 1,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					color: "text.secondary",
				}}
			>
				<Typography variant="body2">No passes assigned</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ flex: 1, minHeight: 0 }}>
			<OneLineCellStyles />
			<AgGridReact<BarrelLayout>
				rowData={layouts}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				rowHeight={28}
				headerHeight={28}
				suppressCellFocus
				suppressContextMenu
				suppressColumnVirtualisation
				animateRows={false}
			/>
		</Box>
	);
};
